import React, { useEffect, useMemo, useImperativeHandle, forwardRef, useState, useRef } from 'react';
import { Player, PlayerRef as RemotionPlayerRef } from '@remotion/player';
import { AbsoluteFill } from 'remotion';
import { Spin } from 'antd';
import { TransitionComposition } from './TransitionComposition';
import { CompositorManifest } from '../types/manifest';
import { TransitionCompositorPlayerProps, TransitionCompositorPlayerRef } from '../types/transition';
import { useSourceResolver } from '../hooks/useSourceResolver';
import { useCompositorManifest } from '../hooks/useCompositorManifest';
import { createFromSceneWindowedManifest, createToSceneWindowedManifest } from '../utils/manifestWindowUtils';
import { calculateTransitionDurations } from '../utils/transitionMapper';
import { DEFAULT_FPS } from '../constants';
import { framesToMs, msToFrames } from '../utils/frameCalculator';
import { TransitionType } from '../../../../types/scene';

export type { TransitionCompositorPlayerRef } from '../types/transition';

async function preloadMedia(url: string, type: 'video' | 'image'): Promise<void> {
  return new Promise((resolve, reject) => {
    if (type === 'video') {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;

      const isBlobUrl = url.startsWith('blob:');

      if (!isBlobUrl) {
        video.crossOrigin = 'anonymous';
      }

      let resolved = false;

      const cleanup = () => {
        if (resolved) return;
        resolved = true;
        video.removeEventListener('canplaythrough', handleReady);
        video.removeEventListener('loadeddata', handleReady);
        video.removeEventListener('loadedmetadata', handleMetadata);
        video.removeEventListener('error', handleError);
        video.pause();
        video.src = '';
        video.load();
      };

      const handleMetadata = () => {
        if (isBlobUrl) {
          cleanup();
          resolve();
        }
      };

      const handleReady = () => {
        cleanup();
        resolve();
      };

      const handleError = () => {
        cleanup();
        reject(new Error(`Failed to preload video: ${url}`));
      };

      video.addEventListener('canplaythrough', handleReady);
      video.addEventListener('loadeddata', handleReady);
      video.addEventListener('loadedmetadata', handleMetadata);
      video.addEventListener('error', handleError);
      video.src = url;
      video.load();

      const timeout = isBlobUrl ? 2000 : 10000;
      setTimeout(() => {
        if (!resolved) {
          console.warn(`[preloadMedia] Timeout (${timeout}ms):`, url.slice(-40));
          cleanup();
          resolve();
        }
      }, timeout);
    } else {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
      img.src = url;
      setTimeout(() => resolve(), 5000);
    }
  });
}

interface TransitionCompositionWrapperProps {
  fromManifest: CompositorManifest | null;
  toManifest: CompositorManifest | null;
  transitionType: TransitionType;
  transitionDurationFrames: number;
  fromClipFrames: number;
  toClipFrames: number;
  canvasWidth: number;
  canvasHeight: number;
}

const TransitionCompositionWrapper: React.FC<TransitionCompositionWrapperProps> = (props) => {
  if (!props.fromManifest && !props.toManifest) {
    return <AbsoluteFill style={{ backgroundColor: '#1a1a1a' }} />;
  }
  return <TransitionComposition {...props} />;
};

export const TransitionCompositorPlayer = forwardRef<TransitionCompositorPlayerRef, TransitionCompositorPlayerProps>(
  (
    {
      fromRecordingData,
      toRecordingData,
      fromPendingBlobs = new Map(),
      toPendingBlobs = new Map(),
      fromPendingBlobUrls,
      toPendingBlobUrls,
      transition,
      clipDurationSeconds = 1.0,
      useFullScenes = false,
      autoPlay = false,
      loop = false,
      showControls = true,
      fps = DEFAULT_FPS,
      style,
      className,
      onTimeUpdate,
      onPlay,
      onPause,
      onEnded,
      onError,
      onReady,
    },
    ref
  ) => {
    const playerRef = useRef<RemotionPlayerRef>(null);
    const { resolver: fromResolver, cleanup: cleanupFromResolver } = useSourceResolver();
    const { resolver: toResolver, cleanup: cleanupToResolver } = useSourceResolver();
    const [mediaPreloaded, setMediaPreloaded] = useState(false);
    const preloadedIdRef = useRef<string | null>(null);

    console.log('[TransitionCompositorPlayer] INIT - fromRecordingData:', {
      hasData: !!fromRecordingData,
      duration: fromRecordingData?.duration,
      streams: {
        webcam: fromRecordingData?.streams?.webcam?.segments?.length,
        screen: fromRecordingData?.streams?.screen?.segments?.length,
        audio: fromRecordingData?.streams?.audio?.segments?.length,
      }
    });
    console.log('[TransitionCompositorPlayer] INIT - toRecordingData:', {
      hasData: !!toRecordingData,
      duration: toRecordingData?.duration,
      streams: {
        webcam: toRecordingData?.streams?.webcam?.segments?.length,
        screen: toRecordingData?.streams?.screen?.segments?.length,
        audio: toRecordingData?.streams?.audio?.segments?.length,
      }
    });

    const fromFullManifest = useCompositorManifest({
      recordingData: fromRecordingData,
      pendingBlobs: fromPendingBlobs,
      pendingBlobUrls: fromPendingBlobUrls,
      sourceResolver: fromResolver,
      fps,
    });

    const toFullManifest = useCompositorManifest({
      recordingData: toRecordingData,
      pendingBlobs: toPendingBlobs,
      pendingBlobUrls: toPendingBlobUrls,
      sourceResolver: toResolver,
      fps,
    });

    console.log('[TransitionCompositorPlayer] MANIFESTS - fromFullManifest:', {
      id: fromFullManifest?.id,
      durationMs: fromFullManifest?.durationMs,
      durationFrames: fromFullManifest?.durationFrames,
      layerCount: fromFullManifest?.layers?.length,
      layers: fromFullManifest?.layers?.map(l => ({
        type: l.type,
        visible: l.visible,
        segmentCount: l.segments?.length,
      })),
    });
    console.log('[TransitionCompositorPlayer] MANIFESTS - toFullManifest:', {
      id: toFullManifest?.id,
      durationMs: toFullManifest?.durationMs,
      durationFrames: toFullManifest?.durationFrames,
      layerCount: toFullManifest?.layers?.length,
      layers: toFullManifest?.layers?.map(l => ({
        type: l.type,
        visible: l.visible,
        segmentCount: l.segments?.length,
      })),
    });

    const durations = useMemo(() => {
      const fromDurationMs = fromRecordingData?.duration || 0;
      const toDurationMs = toRecordingData?.duration || 0;
      const transitionDurationSeconds = transition.type === 'cut' ? 0 : (transition.duration || 1.0);
      
      if (useFullScenes) {
        // Full scene mode: use complete scene durations
        const transitionDurationFrames = Math.max(1, Math.round(transitionDurationSeconds * fps));
        const fromDurationFrames = Math.ceil((fromDurationMs / 1000) * fps);
        const toDurationFrames = Math.ceil((toDurationMs / 1000) * fps);
        const totalDurationFrames = fromDurationFrames + toDurationFrames - transitionDurationFrames;
        
        console.log('[TransitionCompositorPlayer] FULL SCENE MODE durations:', {
          fromDurationMs,
          toDurationMs,
          fromDurationFrames,
          toDurationFrames,
          transitionDurationFrames,
          totalDurationFrames,
        });
        
        return {
          fromClipDurationMs: fromDurationMs,
          toClipDurationMs: toDurationMs,
          transitionDurationFrames,
          totalDurationFrames,
        };
      }
      
      return calculateTransitionDurations(
        fromDurationMs,
        toDurationMs,
        transitionDurationSeconds,
        clipDurationSeconds,
        fps
      );
    }, [fromRecordingData?.duration, toRecordingData?.duration, transition, clipDurationSeconds, fps, useFullScenes]);

    // In full scene mode, use the full manifests directly; otherwise use windowed manifests
    const fromManifest = useMemo(() => {
      if (!fromFullManifest) {
        console.log('[TransitionCompositorPlayer] fromManifest: null (no full manifest)');
        return null;
      }
      
      if (useFullScenes) {
        console.log('[TransitionCompositorPlayer] FULL SCENE MODE - using full fromManifest:', {
          id: fromFullManifest.id,
          durationMs: fromFullManifest.durationMs,
          durationFrames: fromFullManifest.durationFrames,
          layerCount: fromFullManifest.layers?.length,
        });
        return fromFullManifest;
      }
      
      const windowed = createFromSceneWindowedManifest(fromFullManifest, durations.fromClipDurationMs);
      console.log('[TransitionCompositorPlayer] CLIP MODE - fromManifest:', {
        id: windowed?.id,
        durationMs: windowed?.durationMs,
        durationFrames: windowed?.durationFrames,
        clipDurationMs: durations.fromClipDurationMs,
      });
      return windowed;
    }, [fromFullManifest, durations.fromClipDurationMs, useFullScenes]);

    const toManifest = useMemo(() => {
      if (!toFullManifest) {
        console.log('[TransitionCompositorPlayer] toManifest: null (no full manifest)');
        return null;
      }
      
      if (useFullScenes) {
        console.log('[TransitionCompositorPlayer] FULL SCENE MODE - using full toManifest:', {
          id: toFullManifest.id,
          durationMs: toFullManifest.durationMs,
          durationFrames: toFullManifest.durationFrames,
          layerCount: toFullManifest.layers?.length,
        });
        return toFullManifest;
      }
      
      const windowed = createToSceneWindowedManifest(toFullManifest, durations.toClipDurationMs);
      console.log('[TransitionCompositorPlayer] CLIP MODE - toManifest:', {
        id: windowed?.id,
        durationMs: windowed?.durationMs,
        durationFrames: windowed?.durationFrames,
        clipDurationMs: durations.toClipDurationMs,
      });
      return windowed;
    }, [toFullManifest, durations.toClipDurationMs, useFullScenes]);

    const canvasConfig = useMemo(() => {
      const fromCanvas = fromRecordingData?.layout?.canvas;
      const toCanvas = toRecordingData?.layout?.canvas;
      return {
        width: fromCanvas?.width || toCanvas?.width || 1920,
        height: fromCanvas?.height || toCanvas?.height || 1080,
        aspectRatio: fromCanvas?.aspectRatio || toCanvas?.aspectRatio || '16:9',
      };
    }, [fromRecordingData?.layout?.canvas, toRecordingData?.layout?.canvas]);

    const allSourcesReady = useMemo(() => {
      const checkManifest = (manifest: CompositorManifest | null): boolean => {
        if (!manifest) return true;
        for (const layer of manifest.layers) {
          for (const segment of layer.segments) {
            if (segment.source.status === 'loading') return false;
          }
        }
        if (manifest.audioLayer?.segments) {
          for (const segment of manifest.audioLayer.segments) {
            if (segment.source.status === 'loading') return false;
          }
        }
        return true;
      };
      return checkManifest(fromManifest) && checkManifest(toManifest);
    }, [fromManifest, toManifest]);

    useEffect(() => {
      if (!allSourcesReady) return;
      
      const manifests = [fromManifest, toManifest].filter(Boolean);
      if (manifests.length === 0) {
        setMediaPreloaded(true);
        return;
      }
      
      const manifestId = manifests.map(m => m?.id).join('-');
      if (preloadedIdRef.current === manifestId) return;
      
      const preloadAllMedia = async () => {
        setMediaPreloaded(false);
        
        const mediaToPreload: { url: string; type: 'video' | 'image' }[] = [];
        
        for (const manifest of manifests) {
          if (!manifest) continue;
          for (const layer of manifest.layers) {
            for (const segment of layer.segments) {
              if (segment.isAvatar && segment.avatarUrl) {
                mediaToPreload.push({ url: segment.avatarUrl, type: 'image' });
              } else if (segment.source.status === 'ready' && segment.source.value) {
                mediaToPreload.push({ url: segment.source.value, type: 'video' });
              }
            }
          }
          if (manifest.audioLayer?.segments) {
            for (const segment of manifest.audioLayer.segments) {
              if (segment.source.status === 'ready' && segment.source.value) {
                mediaToPreload.push({ url: segment.source.value, type: 'video' });
              }
            }
          }
          if (manifest.background.type === 'image' && manifest.background.imageUrl) {
            mediaToPreload.push({ url: manifest.background.imageUrl, type: 'image' });
          }
        }
        
        console.log('[TransitionCompositorPlayer] Preloading media:', mediaToPreload.length, 'files');
        
        try {
          await Promise.all(
            mediaToPreload.map(async ({ url, type }) => {
              try {
                await preloadMedia(url, type);
              } catch (err) {
                console.warn('[TransitionCompositorPlayer] Failed to preload:', url, err);
              }
            })
          );
          
          preloadedIdRef.current = manifestId;
          setMediaPreloaded(true);
          console.log('[TransitionCompositorPlayer] All media preloaded');
        } catch (err) {
          console.error('[TransitionCompositorPlayer] Preload error:', err);
          setMediaPreloaded(true);
        }
      };
      
      preloadAllMedia();
    }, [fromManifest, toManifest, allSourcesReady]);

    useEffect(() => {
      if (allSourcesReady && mediaPreloaded && onReady) {
        onReady();
      }
    }, [allSourcesReady, mediaPreloaded, onReady]);

    useEffect(() => {
      return () => {
        cleanupFromResolver();
        cleanupToResolver();
      };
    }, [cleanupFromResolver, cleanupToResolver]);

    const fromClipFrames = Math.ceil((durations.fromClipDurationMs / 1000) * fps);
    const toClipFrames = Math.ceil((durations.toClipDurationMs / 1000) * fps);
    const totalDurationFrames = durations.totalDurationFrames;
    const totalDurationMs = (totalDurationFrames / fps) * 1000;

    console.log('[TransitionCompositorPlayer] Durations:', {
      fromDurationMs: fromRecordingData?.duration,
      toDurationMs: toRecordingData?.duration,
      fromClipDurationMs: durations.fromClipDurationMs,
      toClipDurationMs: durations.toClipDurationMs,
      transitionDurationFrames: durations.transitionDurationFrames,
      fromClipFrames,
      toClipFrames,
      totalDurationFrames,
      fromManifest: fromManifest?.id,
      toManifest: toManifest?.id,
      fromManifestLayers: fromManifest?.layers?.map(l => ({ type: l.type, segmentCount: l.segments?.length })),
      toManifestLayers: toManifest?.layers?.map(l => ({ type: l.type, segmentCount: l.segments?.length })),
      fromFullManifestLayers: fromFullManifest?.layers?.map(l => ({ type: l.type, segmentCount: l.segments?.length })),
    });

    useEffect(() => {
      const player = playerRef.current;
      if (!player) return;

      const handleFrameChange = () => {
        if (onTimeUpdate) {
          const frame = player.getCurrentFrame();
          const timeMs = framesToMs(frame, fps);
          onTimeUpdate(timeMs, frame);
        }
      };

      const handlePlayEvent = () => onPlay?.();
      const handlePauseEvent = () => onPause?.();
      const handleEndedEvent = () => onEnded?.();
      const handleErrorEvent = (e: { detail: { error: Error } }) => onError?.(e.detail.error);

      player.addEventListener('frameupdate', handleFrameChange);
      player.addEventListener('play', handlePlayEvent);
      player.addEventListener('pause', handlePauseEvent);
      player.addEventListener('ended', handleEndedEvent);
      player.addEventListener('error', handleErrorEvent);

      return () => {
        player.removeEventListener('frameupdate', handleFrameChange);
        player.removeEventListener('play', handlePlayEvent);
        player.removeEventListener('pause', handlePauseEvent);
        player.removeEventListener('ended', handleEndedEvent);
        player.removeEventListener('error', handleErrorEvent);
      };
    }, [fps, onTimeUpdate, onPlay, onPause, onEnded, onError]);

    useImperativeHandle(
      ref,
      () => ({
        play: () => { playerRef.current?.play(); },
        pause: () => { playerRef.current?.pause(); },
        seekTo: (frame: number) => { playerRef.current?.seekTo(frame); },
        seekToMs: (ms: number) => {
          const frame = msToFrames(ms, fps);
          playerRef.current?.seekTo(frame);
        },
        seekToSeconds: (seconds: number) => {
          const frame = msToFrames(seconds * 1000, fps);
          playerRef.current?.seekTo(frame);
        },
        getCurrentFrame: () => playerRef.current?.getCurrentFrame() ?? 0,
        getCurrentTimeMs: () => {
          const frame = playerRef.current?.getCurrentFrame() ?? 0;
          return framesToMs(frame, fps);
        },
        isPlaying: () => playerRef.current?.isPlaying() ?? false,
        toggle: () => { playerRef.current?.toggle(); },
        getTotalDurationMs: () => totalDurationMs,
      }),
      [fps, totalDurationMs]
    );

    const inputProps = useMemo(() => ({
      fromManifest: fromManifest,
      toManifest: toManifest,
      transitionType: transition.type,
      transitionDurationFrames: durations.transitionDurationFrames,
      fromClipFrames,
      toClipFrames,
      canvasWidth: canvasConfig.width,
      canvasHeight: canvasConfig.height,
    }), [fromManifest, toManifest, transition.type, durations.transitionDurationFrames, fromClipFrames, toClipFrames, canvasConfig]);

    const isLoading = !allSourcesReady || !mediaPreloaded;

    // CRITICAL DEBUG: Compare all frame durations to find mismatch
    const fromManifestVideoSegmentFrames = fromManifest?.layers?.[0]?.segments?.[0]?.durationFrames;
    const fromManifestAudioSegmentFrames = fromManifest?.audioLayer?.segments?.[0]?.durationFrames;
    const toManifestVideoSegmentFrames = toManifest?.layers?.[0]?.segments?.[0]?.durationFrames;
    const toManifestAudioSegmentFrames = toManifest?.audioLayer?.segments?.[0]?.durationFrames;
    
    console.log('[TransitionCompositorPlayer] ⚠️ FRAME DURATION COMPARISON:', {
      from: {
        clipFrames: fromClipFrames,
        manifestDurationFrames: fromManifest?.durationFrames,
        videoSegmentFrames: fromManifestVideoSegmentFrames,
        audioSegmentFrames: fromManifestAudioSegmentFrames,
        MISMATCH: fromClipFrames !== fromManifest?.durationFrames || 
                  fromClipFrames !== fromManifestVideoSegmentFrames ||
                  (fromManifestAudioSegmentFrames && fromClipFrames !== fromManifestAudioSegmentFrames),
      },
      to: {
        clipFrames: toClipFrames,
        manifestDurationFrames: toManifest?.durationFrames,
        videoSegmentFrames: toManifestVideoSegmentFrames,
        audioSegmentFrames: toManifestAudioSegmentFrames,
        MISMATCH: toClipFrames !== toManifest?.durationFrames || 
                  toClipFrames !== toManifestVideoSegmentFrames ||
                  (toManifestAudioSegmentFrames && toClipFrames !== toManifestAudioSegmentFrames),
      },
      transition: {
        type: transition.type,
        durationFrames: durations.transitionDurationFrames,
        durationSeconds: durations.transitionDurationFrames / fps,
      },
      total: {
        totalDurationFrames,
        expectedFromSceneEnd: fromClipFrames,
        transitionStartsAtFrame: fromClipFrames - durations.transitionDurationFrames,
      },
    });

    console.log('[TransitionCompositorPlayer] RENDER STATE:', {
      isLoading,
      allSourcesReady,
      mediaPreloaded,
      fromManifest: !!fromManifest,
      toManifest: !!toManifest,
      totalDurationFrames,
      fromClipFrames,
      toClipFrames,
      transitionType: transition.type,
      transitionDurationFrames: durations.transitionDurationFrames,
    });

    if (isLoading) {
      return (
        <div
          style={{
            ...style,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#1a1a1a',
            color: '#888',
            minHeight: 200,
            aspectRatio: canvasConfig.aspectRatio.replace(':', '/'),
            gap: 12,
          }}
          className={className}
        >
          <Spin size="large" />
          <span style={{ fontSize: 14 }}>
            {!allSourcesReady ? 'Resolving sources...' : 'Buffering video for smooth playback...'}
          </span>
        </div>
      );
    }

    return (
      <Player
        ref={playerRef}
        component={TransitionCompositionWrapper as React.ComponentType<unknown>}
        inputProps={inputProps}
        durationInFrames={Math.max(1, totalDurationFrames)}
        compositionWidth={canvasConfig.width}
        compositionHeight={canvasConfig.height}
        fps={fps}
        style={{
          width: '100%',
          ...style,
        }}
        className={className}
        controls={showControls}
        autoPlay={autoPlay}
        loop={loop}
        clickToPlay={true}
        doubleClickToFullscreen={true}
        spaceKeyToPlayOrPause={true}
        renderPlayPauseButton={undefined}
      />
    );
  }
);

TransitionCompositorPlayer.displayName = 'TransitionCompositorPlayer';

export default TransitionCompositorPlayer;
